/**
 * IP授权验证服务
 * 负责处理IP验证逻辑和状态管理
 */
import proxyConfig from '@/config/proxyConfig'

class IpAuthService {
  constructor() {
    this.isAuthenticated = false
    this.localIp = ''
    this.allowedIps = []
    this.verificationTimer = null
    this.verificationFailCount = 0
    this.maxFailCount = 3
    this.authCallbacks = {
      onSuccess: [],
      onFailed: []
    }
  }

  /**
   * 添加验证状态监听器
   * @param {string} event - 事件类型 ('success' | 'failed')
   * @param {function} callback - 回调函数
   */
  on(event, callback) {
    if (event === 'success') {
      this.authCallbacks.onSuccess.push(callback)
    } else if (event === 'failed') {
      this.authCallbacks.onFailed.push(callback)
    }
  }

  /**
   * 移除验证状态监听器
   * @param {string} event - 事件类型
   * @param {function} callback - 回调函数
   */
  off(event, callback) {
    if (event === 'success') {
      const index = this.authCallbacks.onSuccess.indexOf(callback)
      if (index > -1) {
        this.authCallbacks.onSuccess.splice(index, 1)
      }
    } else if (event === 'failed') {
      const index = this.authCallbacks.onFailed.indexOf(callback)
      if (index > -1) {
        this.authCallbacks.onFailed.splice(index, 1)
      }
    }
  }

  /**
   * 触发事件回调
   * @param {string} event - 事件类型
   * @param {*} data - 传递的数据
   */
  emit(event, data) {
    if (event === 'success') {
      this.authCallbacks.onSuccess.forEach(callback => callback(data))
    } else if (event === 'failed') {
      this.authCallbacks.onFailed.forEach(callback => callback(data))
    }
  }

  /**
   * 获取本机IP地址
   * @returns {Promise<string>} IP地址
   */
  async getLocalIp() {
    try {
      // 优先使用WebRTC获取公网IP
      const webrtcIp = await this.getLocalIpViaWebRTC()
      if (webrtcIp) {
        this.localIp = webrtcIp
        return webrtcIp
      }
      
      // WebRTC获取失败，使用HTTP接口作为兜底方案
      const publicIp = await this.getPublicIpViaHttp()
      if (publicIp) {
        this.localIp = publicIp
        return publicIp
      }
      
      throw new Error('无法获取IP地址')
    } catch (error) {
      throw new Error('无法获取IP地址，请检查网络连接')
    }
  }

  /**
   * 通过WebRTC获取所有可能的IP地址
   * @returns {Promise<string|null>}
   */
  getLocalIpViaWebRTC() {
    return new Promise((resolve) => {
      const pc = new RTCPeerConnection({
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' },
          { urls: 'stun:stun2.l.google.com:19302' }
        ]
      })
      
      const ips = new Set() // 使用Set避免重复IP
      let candidateCount = 0
      const maxCandidates = 10 // 最多收集10个候选
      
      pc.createDataChannel('')
      pc.createOffer()
        .then(offer => pc.setLocalDescription(offer))
        .catch(error => {
          pc.close()
          resolve(null)
        })
      
      pc.onicecandidate = (event) => {
        if (event.candidate) {
          const candidate = event.candidate.candidate
          const ipMatch = candidate.match(/(\d+\.\d+\.\d+\.\d+)/)
          if (ipMatch && !ipMatch[1].startsWith('127.') && !ipMatch[1].startsWith('192.') && !ipMatch[1].startsWith('10.')) {
            ips.add(ipMatch[1])
            candidateCount++
            
            // 如果收集到足够的候选或者已经有公网IP，提前结束
            if (candidateCount >= maxCandidates || this.isPublicIp(ipMatch[1])) {
              pc.close()
              resolve(this.selectBestIp(Array.from(ips)))
            }
          }
        }
      }
      
      // 超时处理 - 延长到5秒以收集更多候选
      setTimeout(() => {
        pc.close()
        if (ips.size > 0) {
          resolve(this.selectBestIp(Array.from(ips)))
        } else {
          resolve(null)
        }
      }, 5000)
    })
  }

  /**
   * 通过HTTP接口获取公网IP（兜底方案）
   * @returns {Promise<string|null>}
   */
  async getPublicIpViaHttp() {
    // 多个备用接口，提高成功率
    const services = [
      {
        url: 'https://httpbin.org/ip',
        parser: (data) => data.origin
      },
      {
        url: 'https://api.ipify.org?format=json',
        parser: (data) => data.ip
      },
      {
        url: 'https://ipapi.co/ip/',
        parser: (data) => typeof data === 'string' ? data.trim() : data,
        isText: true
      }
    ]
    
    for (const service of services) {
      try {
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 3000)
        
        const response = await fetch(service.url, {
          method: 'GET',
          signal: controller.signal
        })
        
        clearTimeout(timeoutId)
        
        if (!response.ok) continue
        
        const data = service.isText ? await response.text() : await response.json()
        const ip = service.parser(data)
        
        if (ip && this.isValidIp(ip)) {
          return ip
        }
      } catch (error) {
        // 继续尝试下一个服务
        continue
      }
    }
    
    return null
  }
  
  /**
   * 验证IP地址格式
   * @param {string} ip - IP地址
   * @returns {boolean}
   */
  isValidIp(ip) {
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/
    return ipRegex.test(ip)
  }
  
  /**
   * 判断是否为公网IP
   * @param {string} ip - IP地址
   * @returns {boolean}
   */
  isPublicIp(ip) {
    const parts = ip.split('.').map(Number)
    
    // 私有IP地址范围
    if (parts[0] === 10) return false // 10.0.0.0/8
    if (parts[0] === 172 && parts[1] >= 16 && parts[1] <= 31) return false // 172.16.0.0/12
    if (parts[0] === 192 && parts[1] === 168) return false // 192.168.0.0/16
    if (parts[0] === 169 && parts[1] === 254) return false // 169.254.0.0/16 (链路本地)
    
    return true
  }
  
  /**
   * 选择最佳IP地址
   * @param {Array<string>} ips - IP地址数组
   * @returns {string|null}
   */
  selectBestIp(ips) {
    if (ips.length === 0) return null
    
    // 优先选择公网IP
    const publicIps = ips.filter(ip => this.isPublicIp(ip))
    if (publicIps.length > 0) {
      return publicIps[0] // 返回第一个公网IP
    }
    
    // 如果没有公网IP，按优先级选择私有IP
    // 优先级：192.168.x.x > 10.x.x.x > 172.16-31.x.x
    const privateIps = ips.filter(ip => !this.isPublicIp(ip))
    const sorted = privateIps.sort((a, b) => {
      const aFirst = parseInt(a.split('.')[0])
      const bFirst = parseInt(b.split('.')[0])
      
      if (aFirst === 192 && bFirst !== 192) return -1
      if (bFirst === 192 && aFirst !== 192) return 1
      if (aFirst === 10 && bFirst === 172) return -1
      if (bFirst === 10 && aFirst === 172) return 1
      
      return 0
    })
    
    return sorted[0] || ips[0]
  }

  /**
   * 验证IP地址
   * @returns {Promise<boolean>} 验证结果
   */
  async verifyIpPermanent () {
    try {
      if (!this.localIp) {
        await this.getLocalIp()
      }

      // 根据代理配置获取IP验证接口URL
      const ipVerifyUrl = proxyConfig.getIpVerifyUrl()

      
      const response = await fetch(ipVerifyUrl, {
        method: 'GET',
        timeout: 10000 // 10秒超时
      })

      if (!response.ok) {
        throw new Error(`验证服务响应错误: ${response.status} ${response.statusText}`)
      }

      const ipListText = await response.text()
      this.allowedIps = ipListText.split(',')
        .map(ip => ip.trim())
        .filter(ip => ip && ip.match(/^\d+\.\d+\.\d+\.\d+$/))

      // IP验证信息已获取

      const isAuthorized = this.allowedIps.includes(this.localIp)
      this.isAuthenticated = isAuthorized

      if (isAuthorized) {
        this.startPeriodicVerification()
        this.emit('success', {
          localIp: this.localIp,
          allowedIps: this.allowedIps
        })
      } else {
        this.stopPeriodicVerification()
        this.emit('failed', {
          localIp: this.localIp,
          allowedIps: this.allowedIps,
          message: `IP地址 ${this.localIp} 未在授权列表中`
        })
      }

      return isAuthorized
    } catch (error) {
      this.isAuthenticated = false
      this.stopPeriodicVerification()
      this.emit('failed', {
        localIp: this.localIp,
        allowedIps: this.allowedIps,
        message: error.message
      })
      throw error
    }
  }



  /**
   * 停止定时验证
   */
  stopPeriodicVerification() {
    if (this.verificationTimer) {
      clearInterval(this.verificationTimer)
      this.verificationTimer = null
      this.networkErrorCount = 0
    }
  }

  /**
   * 获取当前验证状态
   * @returns {boolean}
   */
  getAuthStatus() {
    return this.isAuthenticated
  }

  /**
   * 获取本机IP
   * @returns {string}
   */
  getCurrentIp() {
    return this.localIp
  }

  /**
   * 获取授权IP列表
   * @returns {Array<string>}
   */
  getAllowedIps() {
    return [...this.allowedIps]
  }

  /**
   * 启动定时验证（每6分钟）
   */
  startPeriodicVerification() {
    // 清除之前的定时器
    if (this.verificationTimer) {
      clearInterval(this.verificationTimer)
    }
    
    // 重置失败计数器
    this.verificationFailCount = 0
    
    // 每6分钟验证一次
    this.verificationTimer = setInterval(async () => {
      try {
        // 使用加密POST请求进行定时验证
        const CryptoUtils = (await import('@/utils/cryptoUtils')).default
        const cryptoUtils = new CryptoUtils()
        const encryptedPayload = cryptoUtils.createEncryptedPayload(this.localIp)
        
        // 根据代理配置获取IP验证接口URL进行定期验证
        const apiUrl = proxyConfig.getIpVerifyUrl()
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Host': 'hongchen8.asia:19107',
            'Content-Type': 'application/x-www-form-urlencoded',
            'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/122.0.0.0 Safari/537.36',
            'Accept': '*/*',
            'Origin': 'https://hongchen8.asia:19107',
            'Sec-Fetch-Site': 'same-origin',
            'Sec-Fetch-Mode': 'cors',
            'Sec-Fetch-Dest': 'empty',
            'Referer': 'https://hongchen8.asia:19107/',
            'Accept-Language': 'zh-CN,zh;q=0.9'
          },
          body: `data=${encodeURIComponent(encryptedPayload)}`
        })
        
        if (!response.ok) {
          throw new Error('验证接口请求失败')
        }
        
        // 获取后端返回的JSON响应
        const jsonResponse = await response.text()
        
        try {
          // 解析JSON响应
          const responseObj = JSON.parse(jsonResponse)
          
          // 检查响应码 - 按照原来verifyIpPermanent 的逻辑处理
          if (responseObj.code === 200) {
            // 验证成功，继续处理data字段
            if (!responseObj.data) {
              // code=200但没有data，认为验证失败
              this.emit('failed')
              this.stopPeriodicVerification()
              return
            }
          } else {
            // code不等于200（如403等），按照原逻辑立即跳转
            this.emit('failed')
            this.stopPeriodicVerification()
            return
          }
          
          try {
            // 使用RSA公钥解密后端返回的数据
            const decryptedData = cryptoUtils.rsaDecrypt(responseObj.data)
            
            // 使用新的验证方法：检查时间戳（10秒内）和IP匹配
            const validationResult = cryptoUtils.validateDecryptedData(decryptedData, this.localIp)
            
            if (validationResult.success) {
              // 验证成功，清零失败计数器
              this.verificationFailCount = 0
            } else {
              // 验证失败，立即跳转
              this.emit('failed')
              this.stopPeriodicVerification()
              return
            }
          } catch (decryptError) {
            // RSA解密失败认为有返回值的失败，立即跳转
            this.emit('failed')
            this.stopPeriodicVerification()
            return
          }
        } catch (parseError) {
          // JSON解析失败认为有返回值的失败，立即跳转
          this.emit('failed')
          this.stopPeriodicVerification()
          return
        }
      } catch (error) {
        // 网络错误是真正的“无返回值”失败，累计3次后才跳转
        this.verificationFailCount++
        
        // 网络错误累计3次后才跳转
        if (this.verificationFailCount >= 3) {
          this.emit('failed')
          this.stopPeriodicVerification()
        }
      }
    }, 6 * 60 * 1000) // 6分钟
  }

  /**
   * 重置验证状态
   */
  reset() {
    this.isAuthenticated = false
    this.localIp = ''
    this.allowedIps = []
    this.stopPeriodicVerification()
  }

  /**
   * 销毁服务
   */
  destroy() {
    this.stopPeriodicVerification()
    this.authCallbacks.onSuccess = []
    this.authCallbacks.onFailed = []
    this.reset()
  }
}

// 创建单例实例
const ipAuthService = new IpAuthService()

export default ipAuthService



// WEBPACK FOOTER //
// ./src/services/ipAuthService.js